import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Budget, CompanyInfo, BudgetItem } from '../types';

export class BudgetService {
  private static instance: BudgetService;
  private currentBudgetNumber: number = 0;

  public static getInstance(): BudgetService {
    if (!BudgetService.instance) {
      BudgetService.instance = new BudgetService();
    }
    return BudgetService.instance;
  }

  // Obtener el siguiente número de presupuesto secuencial
  private async getNextBudgetNumber(userId: string): Promise<string> {
    try {
      const year = new Date().getFullYear();
      const yearPrefix = year.toString().slice(-2);

      // Buscar todos los presupuestos del año actual
      const budgetsRef = collection(db, 'budgets');
      const q = query(budgetsRef, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);

      // Filtrar presupuestos del año actual y encontrar el número más alto
      let maxSequence = 0;
      const yearPattern = new RegExp(`^PRES-${yearPrefix}-(\\d+)$`);

      querySnapshot.docs.forEach(doc => {
        const budget = doc.data() as Budget;
        const budgetNumber = budget.budgetNumber;
        const match = budgetNumber.match(yearPattern);

        if (match) {
          const sequence = parseInt(match[1]);
          if (sequence > maxSequence) {
            maxSequence = sequence;
          }
        }
      });

      // Generar el siguiente número secuencial
      const nextSequence = maxSequence + 1;
      return `PRES-${yearPrefix}-${nextSequence.toString().padStart(4, '0')}`;
    } catch (error) {
      console.error('Error obteniendo número de presupuesto:', error);
      // Fallback: usar timestamp
      const year = new Date().getFullYear();
      const yearPrefix = year.toString().slice(-2);
      const timestamp = Date.now().toString().slice(-6);
      const sequence = parseInt(timestamp) % 10000;
      return `PRES-${yearPrefix}-${sequence.toString().padStart(4, '0')}`;
    }
  }

  // Generar PDF del presupuesto
  async generateBudgetPDF(budget: Budget): Promise<Blob> {
    try {
      const htmlContent = this.generateBudgetHTML(budget);

      // Crear un elemento temporal en el DOM para renderizar el HTML
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.top = '0';
      tempDiv.style.left = '0';
      tempDiv.style.width = '210mm'; // Ancho A4
      tempDiv.style.padding = '20mm';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.zIndex = '9999';
      tempDiv.style.visibility = 'visible';
      tempDiv.style.opacity = '1';
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      try {
        // Esperar un poco para asegurar que todo esté renderizado
        await new Promise(resolve => setTimeout(resolve, 500));

        // Convertir HTML a canvas usando html2canvas
        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: tempDiv.scrollWidth,
          height: tempDiv.scrollHeight,
          windowWidth: tempDiv.scrollWidth,
          windowHeight: tempDiv.scrollHeight,
          allowTaint: false,
          imageTimeout: 5000,
        });

        // Verificar que el canvas tenga contenido
        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error(
            'El canvas está vacío. El HTML no se renderizó correctamente.'
          );
        }

        // Crear PDF desde el canvas
        const imgData = canvas.toDataURL('image/png');

        if (!imgData || imgData === 'data:,') {
          throw new Error('No se pudo generar la imagen del PDF.');
        }

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210; // Ancho A4 en mm
        const pageHeight = 297; // Alto A4 en mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Añadir primera página
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Añadir páginas adicionales si es necesario
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        return pdf.output('blob');
      } finally {
        // Limpiar el elemento temporal
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv);
        }
      }
    } catch (error) {
      console.error('Error generando PDF de presupuesto:', error);
      throw new Error(
        `No se pudo generar el PDF del presupuesto: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );
    }
  }

  // Generar HTML para el presupuesto
  private generateBudgetHTML(budget: Budget): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          * { box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 15px; 
            background-color: white;
            color: black;
            font-size: 11px;
            line-height: 1.4;
          }
          .header { 
            text-align: center; 
            margin-bottom: 15px; 
            color: black;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .header h1 { 
            font-size: 24px; 
            font-weight: bold; 
            margin: 5px 0; 
            color: black;
          }
          .header h2 { 
            font-size: 18px; 
            font-weight: bold; 
            margin: 3px 0; 
            color: black;
          }
          .company-info { 
            margin-bottom: 15px; 
            color: black;
          }
          .company-info h3 {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
            color: black;
          }
          .company-info p {
            margin: 2px 0;
            font-size: 10px;
            color: black;
          }
          .budget-details { 
            margin-bottom: 15px; 
            color: black;
          }
          .budget-details p {
            margin: 3px 0;
            font-size: 10px;
            color: black;
          }
          .patient-info { 
            margin-bottom: 15px; 
            color: black;
          }
          .patient-info h3 {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
            color: black;
          }
          .patient-info p {
            margin: 2px 0;
            font-size: 10px;
            color: black;
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 15px; 
            color: black;
            font-size: 10px;
          }
          .items-table th, .items-table td { 
            border: 1px solid #333; 
            padding: 5px; 
            text-align: left; 
            color: black;
            background-color: white;
          }
          .items-table th {
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 10px;
          }
          .totals { 
            text-align: right; 
            margin-bottom: 12px; 
            color: black;
          }
          .totals p {
            margin: 3px 0;
            font-size: 11px;
            color: black;
          }
          .notes-section {
            margin-top: 15px;
            padding: 10px;
            border: 1px solid #333;
            background-color: #f9f9f9;
          }
          .notes-section h4 {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 5px;
            color: black;
          }
          .notes-section p {
            margin: 2px 0;
            font-size: 9px;
            color: black;
          }
          .footer { 
            text-align: center; 
            margin-top: 20px; 
            font-size: 9px; 
            color: black;
            line-height: 1.3;
            border-top: 1px solid #333;
            padding-top: 10px;
          }
          .footer p {
            margin: 2px 0;
            color: black;
          }
          strong {
            font-weight: bold;
            color: black;
          }
          .validity-box {
            background-color: #fff3cd;
            border: 2px solid #ffc107;
            padding: 10px;
            margin: 15px 0;
            border-radius: 5px;
          }
          .validity-box p {
            margin: 3px 0;
            font-size: 10px;
            color: #856404;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PRESUPUESTO</h1>
          <h2>${budget.budgetNumber}</h2>
        </div>
        
        <div class="company-info">
          <h3>${budget.companyInfo.companyName}</h3>
          <p>NIF/CIF: ${budget.companyInfo.taxId}</p>
          <p>${budget.companyInfo.address}</p>
          <p>${budget.companyInfo.postalCode} ${budget.companyInfo.city}, ${budget.companyInfo.province}</p>
          <p>Tel: ${budget.companyInfo.phone} | Email: ${budget.companyInfo.email}</p>
          ${budget.companyInfo.website ? `<p>Web: ${budget.companyInfo.website}</p>` : ''}
        </div>
        
        <div class="budget-details">
          <p><strong>Fecha de emisión:</strong> ${budget.budgetDate.toLocaleDateString('es-ES')}</p>
          <p><strong>Válido hasta:</strong> ${budget.validityDate.toLocaleDateString('es-ES')}</p>
        </div>
        
        <div class="patient-info">
          <h3>Datos del Paciente</h3>
          <p><strong>${budget.patientInfo.name} ${budget.patientInfo.surname}</strong></p>
          <p>DNI: ${budget.patientInfo.dni}</p>
          ${budget.patientInfo.birthDate ? `<p>Fecha de nacimiento: ${budget.patientInfo.birthDate}</p>` : ''}
          ${budget.patientInfo.address ? `<p>Dirección: ${budget.patientInfo.address}</p>` : ''}
          ${budget.patientInfo.city ? `<p>${budget.patientInfo.postalCode || ''} ${budget.patientInfo.city}${budget.patientInfo.province ? `, ${budget.patientInfo.province}` : ''}</p>` : ''}
        </div>
        
        ${budget.medicalJustification ? `
        <div class="notes-section">
          <h4>Justificación Médica</h4>
          <p>${budget.medicalJustification}</p>
        </div>
        ` : ''}
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Cantidad</th>
              <th>Precio Unit.</th>
              <th>Descuento</th>
              <th>Subtotal</th>
              <th>IVA</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${budget.items
              .map(
                item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${item.unitPrice.toFixed(2)}€</td>
                <td>${item.discount ? `${item.discount}%` : '0%'}</td>
                <td>${item.subtotal.toFixed(2)}€</td>
                <td>${item.taxAmount.toFixed(2)}€</td>
                <td>${item.total.toFixed(2)}€</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <p><strong>Subtotal:</strong> ${budget.subtotal.toFixed(2)}€</p>
          ${
            budget.taxAmount > 0
              ? `<p><strong>IVA (${budget.items[0]?.taxRate?.toFixed(0) || 0}%):</strong> ${budget.taxAmount.toFixed(2)}€</p>`
              : '<p><strong>IVA:</strong> Exento (Asistencia sanitaria según Art. 20.1.9º Ley 37/1992)</p>'
          }
          <p style="font-size: 14px; font-weight: bold; margin-top: 5px;"><strong>TOTAL:</strong> ${budget.total.toFixed(2)}€</p>
        </div>
        
        <div class="validity-box">
          <p>⚠️ Este presupuesto tiene validez hasta el ${budget.validityDate.toLocaleDateString('es-ES')}</p>
        </div>
        
        ${budget.paymentConditions ? `
        <div class="notes-section">
          <h4>Condiciones de Pago</h4>
          <p>${budget.paymentConditions}</p>
        </div>
        ` : ''}
        
        ${budget.notes ? `
        <div class="notes-section">
          <h4>Observaciones</h4>
          <p>${budget.notes}</p>
        </div>
        ` : ''}
        
        ${budget.terms ? `
        <div class="notes-section">
          <h4>Términos y Condiciones</h4>
          <p>${budget.terms}</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Este presupuesto cumple con la normativa vigente de la Región de Murcia</p>
          <p>Asistencia sanitaria exenta de IVA según Art. 20.1.9º Ley 37/1992</p>
          <p>Presupuesto generado automáticamente - ${budget.budgetNumber}</p>
        </div>
      </body>
      </html>
    `;
  }

  // Crear un presupuesto desde datos básicos
  async createBudget(
    userId: string,
    companyInfo: CompanyInfo,
    patientInfo: {
      name: string;
      surname: string;
      dni: string;
      birthDate?: string;
      address?: string;
      city?: string;
      postalCode?: string;
      province?: string;
    },
    items: Omit<BudgetItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>[],
    options?: {
      validityDays?: number;
      medicalJustification?: string;
      paymentConditions?: string;
      notes?: string;
      terms?: string;
    }
  ): Promise<Budget> {
    try {
      // Calcular totales
      const calculatedItems = items.map(item => {
        const subtotal =
          item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
        const taxAmount = subtotal * (item.taxRate / 100);
        const total = subtotal + taxAmount;

        return {
          ...item,
          id: this.generateId(),
          subtotal: Math.round(subtotal * 100) / 100,
          taxAmount: Math.round(taxAmount * 100) / 100,
          total: Math.round(total * 100) / 100,
        };
      });

      const subtotal = calculatedItems.reduce(
        (sum, item) => sum + item.subtotal,
        0
      );
      const taxAmount = calculatedItems.reduce(
        (sum, item) => sum + item.taxAmount,
        0
      );
      const total = subtotal + taxAmount;

      // Generar número de presupuesto
      const budgetNumber = await this.getNextBudgetNumber(userId);

      // Fechas
      const budgetDate = new Date();
      const validityDays = options?.validityDays || 30; // Por defecto 30 días
      const validityDate = new Date(
        budgetDate.getTime() + validityDays * 24 * 60 * 60 * 1000
      );

      // Crear presupuesto
      const budget: Budget = {
        userId,
        budgetNumber,
        budgetDate,
        validityDate,
        patientInfo,
        companyInfo,
        items: calculatedItems,
        subtotal: Math.round(subtotal * 100) / 100,
        taxAmount: Math.round(taxAmount * 100) / 100,
        total: Math.round(total * 100) / 100,
        currency: 'EUR',
        medicalJustification: options?.medicalJustification,
        paymentConditions: options?.paymentConditions,
        notes: options?.notes,
        terms: options?.terms,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      return budget;
    } catch (error) {
      console.error('Error creando presupuesto:', error);
      throw new Error('No se pudo crear el presupuesto');
    }
  }

  // Guardar presupuesto en Firestore
  async saveBudget(budget: Budget): Promise<Budget> {
    try {
      // Preparar datos para Firestore
      const firestoreData: any = {
        userId: budget.userId,
        budgetNumber: budget.budgetNumber,
        budgetDate: budget.budgetDate,
        validityDate: budget.validityDate,
        patientInfo: budget.patientInfo,
        companyInfo: budget.companyInfo,
        items: budget.items,
        subtotal: budget.subtotal,
        taxAmount: budget.taxAmount,
        total: budget.total,
        currency: budget.currency,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Solo añadir campos opcionales si tienen valor
      if (budget.medicalJustification) {
        firestoreData.medicalJustification = budget.medicalJustification;
      }
      if (budget.paymentConditions) {
        firestoreData.paymentConditions = budget.paymentConditions;
      }
      if (budget.notes) {
        firestoreData.notes = budget.notes;
      }
      if (budget.terms) {
        firestoreData.terms = budget.terms;
      }

      // Guardar en Firestore
      const docRef = await addDoc(collection(db, 'budgets'), firestoreData);

      const savedBudget: Budget = {
        ...budget,
        id: docRef.id,
      };

      return savedBudget;
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
      throw new Error('No se pudo guardar el presupuesto');
    }
  }

  // Obtener presupuestos de un usuario
  async getUserBudgets(userId: string): Promise<Budget[]> {
    try {
      const budgetsRef = collection(db, 'budgets');
      const q = query(
        budgetsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc =>
        this.convertFirestoreBudget({ ...doc.data(), id: doc.id })
      );
    } catch (error) {
      console.error('Error obteniendo presupuestos:', error);
      throw new Error('No se pudieron obtener los presupuestos');
    }
  }

  // Obtener un presupuesto por ID
  async getBudgetById(budgetId: string): Promise<Budget | null> {
    try {
      const docRef = doc(db, 'budgets', budgetId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return this.convertFirestoreBudget({
          ...docSnap.data(),
          id: docSnap.id,
        });
      }

      return null;
    } catch (error) {
      console.error('Error obteniendo presupuesto:', error);
      throw new Error('No se pudo obtener el presupuesto');
    }
  }

  // Convertir datos de Firestore a Budget (convertir Timestamps a Date)
  private convertFirestoreBudget(data: any): Budget {
    const convertDate = (dateValue: any): Date => {
      if (!dateValue) return new Date();
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        return dateValue.toDate();
      }
      if (dateValue instanceof Date) {
        return dateValue;
      }
      return new Date(dateValue);
    };

    return {
      ...data,
      budgetDate: convertDate(data.budgetDate),
      validityDate: convertDate(data.validityDate),
      createdAt: convertDate(data.createdAt),
      updatedAt: convertDate(data.updatedAt),
      companyInfo: {
        ...data.companyInfo,
        createdAt: convertDate(data.companyInfo?.createdAt),
        updatedAt: convertDate(data.companyInfo?.updatedAt),
      },
    };
  }

  // Generar ID único
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

export const budgetService = BudgetService.getInstance();

